import React, { memo } from "react";
import { useTranslation } from "react-i18next";
import { HiLightningBolt, HiUsers } from "react-icons/hi";
import { HiTicket } from "react-icons/hi2";
import { IconType } from "react-icons";
import { CustomButton } from "../../atoms/CustomButton";

interface UseCase {
  id: string;
  icon: IconType;
  titleKey: string;
  descriptionKey: string;
}

const USE_CASES: UseCase[] = [
  {
    id: "concerts",
    icon: HiTicket,
    titleKey: "components.eventPayments.emptyState.concerts.title",
    descriptionKey: "components.eventPayments.emptyState.concerts.description",
  },
  {
    id: "conferences",
    icon: HiUsers,
    titleKey: "components.eventPayments.emptyState.conferences.title",
    descriptionKey:
      "components.eventPayments.emptyState.conferences.description",
  },
  {
    id: "workshops",
    icon: HiLightningBolt,
    titleKey: "components.eventPayments.emptyState.workshops.title",
    descriptionKey: "components.eventPayments.emptyState.workshops.description",
  },
];

interface UseCaseCardProps {
  useCase: UseCase;
}

const UseCaseCard: React.FC<UseCaseCardProps> = memo(({ useCase }) => {
  const { t } = useTranslation();
  const Icon = useCase.icon;

  return (
    <div className="rounded-lg border border-gray-200 p-4">
      <div className="mb-2 flex h-8 w-8 items-center justify-center rounded-md bg-gray-50">
        <Icon size={18} className="text-gray-600" aria-hidden="true" />
      </div>
      <h3 className="text-sm font-semibold text-gray-900">
        {t(useCase.titleKey)}
      </h3>
      <p className="mt-0.5 text-xs text-gray-500">
        {t(useCase.descriptionKey)}
      </p>
    </div>
  );
});

UseCaseCard.displayName = "UseCaseCard";

interface EventEmptyStateProps {
  onCreateEvent?: () => void;
}

const EventEmptyState: React.FC<EventEmptyStateProps> = ({ onCreateEvent }) => {
  const { t } = useTranslation();

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6">
      <div className="max-w-3xl">
        <p className="mb-4 text-sm text-gray-500">
          {t("components.eventPayments.emptyState.perfectFor")}
        </p>

        <div
          className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-3"
          role="list"
          aria-label={
            t("components.eventPayments.emptyState.perfectFor") as string
          }
        >
          {USE_CASES.map((useCase) => (
            <UseCaseCard key={useCase.id} useCase={useCase} />
          ))}
        </div>

        <p className="mb-4 text-sm text-gray-500">
          {t("components.eventPayments.emptyState.readyToStart")}
        </p>

        {onCreateEvent && (
          <div className="flex">
            <CustomButton
              title={t("components.eventPayments.actions.createFirstEvent")}
              onClick={onCreateEvent}
              variant="primary"
              icon="plus"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(EventEmptyState);
